import { useState } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { AppointmentRequestFormValues } from "./types";

export const useAppointmentRequest = (
  serviceId: string,
  professionalId: string,
  onOpenChange: (open: boolean) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<AppointmentRequestFormValues>({
    defaultValues: {
      requested_date: new Date().toISOString().split('T')[0],
      phone: "",
      email: "",
      medical_history: "",
      symptoms: "",
      preferred_language: "",
      emergency_contact: "",
      notes: "",
    },
  });

  const onSubmit = async (values: AppointmentRequestFormValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour prendre un rendez-vous",
        });
        return;
      }

      const { error } = await supabase
        .from("professional_appointments")
        .insert({
          professional_id: professionalId,
          patient_id: user.id,
          service_id: serviceId,
          requested_date: values.requested_date,
          notes: `
            Téléphone: ${values.phone}
            Email: ${values.email}
            Antécédents médicaux: ${values.medical_history || 'Non spécifié'}
            Symptômes: ${values.symptoms || 'Non spécifié'}
            Langue préférée: ${values.preferred_language || 'Non spécifié'}
            Contact d'urgence: ${values.emergency_contact || 'Non spécifié'}
            Notes additionnelles: ${values.notes || 'Aucune'}
          `,
          status: "pending",
        });

      if (error) throw error;

      toast({
        title: "Demande envoyée",
        description: "Votre demande de rendez-vous a été envoyée avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["professional-appointments"] });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'envoyer la demande de rendez-vous",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    onSubmit,
  };
};